import React from 'react';
import styled from 'styled-components';
import { media } from '@peloton/styles';
import type { SubsegmentMovement } from '@engage/api-v2';
import { SlideoverStackTransition } from '@members/animations';
import type {
  ScheduledClassDetailsFragment,
  ScheduledUsersFragment,
} from '@members/graphql-swr/schemas/scheduled-classes/queries/shared-fragments.generated';
import lazyLoader from '@members/lazy-loader/LazyLoader';
import { StandardModalContainer, modalHeight } from '@members/modal';
import type { ViewProps } from './ConnectedOnDemandModal';
import { OnDemandModalViewType } from './types';

// prettier-ignore
const SlideoverModalView = lazyLoader(() => import('./Views/ClassDetails/Modal' /* webpackChunkName: "ClassDetailsModal" */));
// prettier-ignore
const SchedulingModalView =  lazyLoader(() => import('./Views/Scheduling/SchedulingModalView' /* webpackChunkName: "SchedulingModalView" */));
// prettier-ignore
const ScheduleClassConfirmationView =  lazyLoader(() => import('./Views/Scheduling/ScheduleClassConfirmationView' /* webpackChunkName: "ScheduleClassConfirmationView" */));
// prettier-ignore
const RescheduleOnDemandClassView =  lazyLoader(() => import('./Views/Scheduling/RescheduleOnDemandClassView' /* webpackChunkName: "RescheduleOnDemandClassView" */));
// prettier-ignore
const MembersYouFollowView =  lazyLoader(() => import('./Views//MembersYouFollow/MembersYouFollowList' /* webpackChunkName: "MembersYouFollowView" */));
// prettier-ignore
const ClassPlanDetailsView =  lazyLoader(() => import('./Views/ClassPlanDetails/ClassPlanDetails' /* webpackChunkName: "ClassPlanDetailsView" */));
// prettier-ignore
const AdditionalInstructorsView =  lazyLoader(() => import('./Views/AdditionalInstructors/AdditionalInstructorsModal' /* webpackChunkName: "AdditionalInstructorsView" */));
// prettier-ignore
const MovementGroupVideoView =  lazyLoader(() => import('./Views/Movements/MovementGroupVideo' /* webpackChunkName: "MovementGroupVideoView" */));

const toViewComponent = (view: OnDemandModalViewType): any => {
  switch (view) {
    case OnDemandModalViewType.ClassDetails:
      return SlideoverModalView;
    case OnDemandModalViewType.ScheduleClass:
      return SchedulingModalView;
    case OnDemandModalViewType.ScheduleClassConfirmation:
      return ScheduleClassConfirmationView;
    case OnDemandModalViewType.RescheduleClass:
      return RescheduleOnDemandClassView;
    case OnDemandModalViewType.AdditionalInstructors:
      return AdditionalInstructorsView;
    case OnDemandModalViewType.MembersYouFollow:
      return MembersYouFollowView;
    case OnDemandModalViewType.ViewDetails:
      return ClassPlanDetailsView;
    case OnDemandModalViewType.ViewMovementGroupVideo:
      return MovementGroupVideoView;
    default:
      return SlideoverModalView;
  }
};

const ModalViewWithNavStack: React.FC<
  React.PropsWithChildren<ViewProps & { shouldShowButtons: boolean }>
> = props => {
  const [currScheduledClass, setCurrScheduledClass] = React.useState<
    (ScheduledClassDetailsFragment & ScheduledUsersFragment) | null
  >(null);
  const [viewStack, setViewStack] = React.useState([OnDemandModalViewType.ClassDetails]);
  const [currentVideoSelected, setCurrentVideoSelected] = React.useState<
    SubsegmentMovement | undefined
  >(undefined);
  const [currentMovementGroup, setCurrentMovementGroup] = React.useState(undefined);

  const goBack = React.useCallback(() => {
    const newStack = [...viewStack];
    newStack.pop();
    setViewStack(newStack);
  }, [viewStack, setViewStack]);

  const navigateTo = React.useCallback(
    (view: OnDemandModalViewType) => {
      setViewStack([...viewStack, view]);
    },
    [viewStack, setViewStack],
  );

  return (
    <ModalContainer data-test-id="classDetailsModal">
      <SlideoverStackTransition>
        {viewStack
          .map(toViewComponent)
          // eslint-disable-next-line react/display-name
          .map((View, idx) => (isEntered: boolean) => (
            <View
              currScheduledClass={currScheduledClass}
              setCurrScheduledClass={setCurrScheduledClass}
              key={idx}
              isEntered={isEntered}
              goBack={goBack}
              lastView={viewStack[viewStack.length - 1]}
              navigateTo={navigateTo}
              scheduleId={currScheduledClass?.id}
              currentVideoSelected={currentVideoSelected}
              setCurrentVideoSelected={setCurrentVideoSelected}
              setCurrentMovementGroup={setCurrentMovementGroup}
              currentMovementGroup={currentMovementGroup}
              {...props}
            />
          ))}
      </SlideoverStackTransition>
    </ModalContainer>
  );
};

const ModalContainer = styled(StandardModalContainer)`
  width: 100%;
  padding: 0;
  text-align: unset;
  ${media.tablet`
    padding: 0;
    height: ${modalHeight};
  `}
`;

export default ModalViewWithNavStack;
